import { useCallback, useMemo, useRef, useState } from 'react';

import { Box, Button, Checkbox, Typography } from '@mui/material';
import { red } from '@mui/material/colors';

import { Promotion, PromotionDuration } from '../../types';
import { PromotionFooter } from '../PromotionFooter/PromotionFooter';
import {
  PromotionSummaryFC,
  PromotionSummaryFCProps,
} from '../PromotionSummary/PromotionSummaryFC';
import { Asterisk } from '@provider-portal/common/components/Asterisk/Asterisk';
import { Stack } from '@provider-portal/design-common/kalep/components';

export interface AddPromotionOverviewFCProps {
  strings: {
    title: string;
    back: string;
    publish: string;
    agreeWithTaC: string;
    promotionVisibilityDisclaimer: string;
  } & PromotionSummaryFCProps['strings'];
  promotion: Promotion;
  duration: PromotionDuration;
  selectedMenuItemNames: string;
  onClickPublish: () => void;
  onClickBack: () => void;
}

export const AddPromotionOverviewFC = ({
  strings,
  promotion,
  duration,
  selectedMenuItemNames,
  onClickPublish,
  onClickBack,
}: AddPromotionOverviewFCProps) => {
  const [isAgreeWithTaC, setIsAgreeWithTaC] = useState(false);
  const [errorStyle, setErrorStyle] = useState(undefined);

  const buttonRef = useRef(null);

  const checkBoxChanged = useCallback(
    (_: unknown, checked: boolean) => {
      setIsAgreeWithTaC(checked);
    },
    [setIsAgreeWithTaC],
  );
  const onPublishButtonClick = useCallback(() => {
    if (isAgreeWithTaC && buttonRef.current && !buttonRef.current.disabled) {
      buttonRef.current.disabled = true;
      onClickPublish();
    }
  }, [isAgreeWithTaC, onClickPublish]);

  const onErrorOccurred = useCallback(() => {
    setErrorStyle({ color: red[500] });
  }, [setErrorStyle]);

  const footer = useMemo(() => {
    const checkBox = (
      <Checkbox checked={isAgreeWithTaC} onChange={checkBoxChanged} sx={errorStyle} />
    );
    return (
      <Stack gap="2rem">
        <PromotionFooter text={strings.agreeWithTaC} preText={checkBox} shouldShowOmnibusDisclaimer>
          <Box mr={1}>
            <Button color="secondary" variant="contained" onClick={onClickBack}>
              {strings.back}
            </Button>
          </Box>
          <Box>
            <span
              role="button"
              tabIndex={0}
              onKeyDown={isAgreeWithTaC ? onPublishButtonClick : onErrorOccurred}
              onClick={isAgreeWithTaC ? onPublishButtonClick : onErrorOccurred}
              style={{ display: 'block' }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={!isAgreeWithTaC}
                ref={buttonRef}
              >
                {strings.publish}
              </Button>
            </span>
          </Box>
        </PromotionFooter>
        <Box mr={1}>
          <Asterisk lines={[strings.promotionVisibilityDisclaimer]} />
        </Box>
      </Stack>
    );
  }, [
    checkBoxChanged,
    errorStyle,
    isAgreeWithTaC,
    onClickBack,
    onErrorOccurred,
    onPublishButtonClick,
    strings.agreeWithTaC,
    strings.back,
    strings.publish,
    strings.promotionVisibilityDisclaimer,
  ]);

  return (
    <Box>
      <Box marginBottom={2}>
        <Typography variant="h6">{strings.title}</Typography>
      </Box>
      <PromotionSummaryFC
        promotion={promotion}
        duration={duration}
        strings={strings}
        footer={footer}
        selectedMenuItemNames={selectedMenuItemNames}
        selectedProductIds={[]}
        exclusionTags={[]}
        hideMenuEditor
        onMenuItemsUpdate={null}
      />
    </Box>
  );
};
